// Full case-study content, ported from buildmatiq.com/work/* and adapted to
// first person. Section fields render in this order inside each card:
// body → pipeline → steps → cards → bullets → features → stack → highlight → body2
// An entry can also be { pair: [sectionA, sectionB] } for a two-column row.

export const caseStudies = {
  structura: {
    title: "Structura — AI-Powered EU Grant Application & Project Management Platform",
    subtitle:
      "A full-lifecycle SaaS platform for authoring, collaborating on, and managing EU funding applications, from blank idea through structured submission to post-award project tracking. It runs on a dual-runtime architecture: Next.js for a fast localised UI and FastAPI for AI, RAG, and document-heavy processing.",
    tags: ["EU Funding / ERDF", "AI / RAG", "Real-time Collaboration", "Full Stack SaaS", "12-Month Build", "Dual Runtime"],
    liveUrl: "https://www.structurafunding.se/en",
    heroImage: { src: "/assets/work/structura/landing.png", alt: "Structura — public landing page at structurafunding.se" },
    stats: [
      { num: "12 mo", label: "End-to-end build" },
      { num: "2", label: "Separate runtimes" },
      { num: "4", label: "Application types" },
      { num: "RAG", label: "AI + vector search" },
      { num: "WS", label: "Real-time collab" },
      { num: "i18n", label: "Multi-language" },
    ],
    sections: [
      {
        label: "The Problem",
        heading: "EU grants fail on process, not project quality",
        body: "Applying for EU funding like ERDF grants is one of the most complex administrative challenges organisations face. Applications are long, heavily structured, require multiple stakeholders, must comply with strict regulatory rules, and often fail simply because of poor writing, missing sections, or misunderstood criteria, not because the project itself is weak.",
        highlight:
          "The client needed a platform that could guide organisations through the entire grant lifecycle: structuring an initial idea, writing the application collaboratively with AI assistance, then managing the funded project with reporting, time tracking, and regulatory compliance. No such tool existed, so I built it from scratch.",
      },
      {
        label: "Architecture",
        heading: "Dual-runtime design",
        body: "One of the core engineering decisions was splitting the system into two purpose-built runtimes, each doing what it does best.",
        cards: [
          { title: "Next.js 15", icon: "🖥️", subtitle: "FRONTEND RUNTIME", body: "React 19, TypeScript, Tailwind CSS 4, next-intl (i18n), NextAuth, Socket.IO client" },
          { title: "FastAPI", icon: "🤖", subtitle: "AI & API RUNTIME", body: "Python, LangChain, Pinecone, Anthropic + OpenAI, Socket.IO, Motor/MongoDB" },
          { title: "MongoDB", icon: "🗄️", subtitle: "DATA & STORAGE", body: "+ Pinecone vectors + AWS S3 + Resend email" },
        ],
      },
      {
        pair: [
          {
            label: "Phase 1",
            heading: "Application Authoring",
            steps: [
              { title: "4 application types", body: "Mini, Pre-study, Main application, and ESF variant, each with its own schema-driven editor route and section layout." },
              { title: "Schema-driven sections", body: "Conditional fields, repeatable groups, rich input types with validation per section." },
              { title: "Context Hub", body: "AI-powered workspace per application where users upload documents and briefings that feed into all AI features." },
              { title: "AI writing assistant", body: "Section-level prompts, autodraft generation, ERDF-specific writer that generates text from context and user inputs." },
              { title: "Streaming validation", body: "Real-time AI validation of sections against funding criteria, cross-validation between sections, smart notes." },
              { title: "Export", body: "Application data exported to formatted documents via client-side generation (docx, PDF)." },
            ],
          },
          {
            label: "Phase 2 & 3",
            heading: "Collaboration + Post-Award",
            steps: [
              { title: "Collaborator invites", body: "Share links, token-based invite flows, role-aware access for internal and external stakeholders." },
              { title: "Threaded comments & real-time chat", body: "Section/application-level comments and Socket.IO chat rooms with read state and unread counts." },
              { title: "RAG chat assistant", body: "Document-aware AI chat that answers questions based on uploaded context and funding guidelines in real time." },
              { title: "Project workspace", body: "Post-award project CRUD, dashboard aggregates, team management, and AI-assisted project import." },
              { title: "Weekly updates + evidence", body: "Structured progress reports with file attachments per week, archived for audit purposes." },
              { title: "Time reporting & compliance", body: "Contributor time logging with approval flows, De Minimis tracking, indicators tracking for EU reporting." },
            ],
          },
        ],
      },
      {
        label: "AI & RAG Features",
        heading: "Where the engineering depth lives",
        cards: [
          { icon: "🧠", title: "RAG Document Search", body: "Documents vectorised via LangChain + Pinecone. All AI features search this vector store for contextually accurate, document-grounded answers." },
          { icon: "✍️", title: "ERDF Writer / Autodraft", body: "AI drafts full application sections based on user context and funding guidelines. Uses Anthropic + OpenAI via LangChain with streaming output." },
          { icon: "✅", title: "Streaming Validation", body: "Sections are validated against ERDF funding rules in real time and streamed character by character to the UI." },
          { icon: "💬", title: "RAG Chat Assistant", body: "Guided conversational assistant with full document awareness, answering questions with source citations." },
          { icon: "📝", title: "Smart Notes", body: "AI extracts key insights, action items, and structured notes from application content. Saved, editable, linked to sections." },
          { icon: "📂", title: "Content Extraction", body: "Uploaded files (PDF, DOCX, XLSX) parsed by PyMuPDF, pdfminer, and mammoth, then the text is extracted and fed into RAG pipelines." },
        ],
      },
      {
        label: "Engineering Challenges",
        heading: "Why this was a year of work",
        bullets: [
          { title: "Dual-runtime auth security", body: "Session cookies managed in Next.js can't safely cross to a separate FastAPI origin. I built a Next.js proxy layer that derives a Bearer token from the session cookie and forwards it, which keeps auth airtight without cross-site cookie risk." },
          { title: "Schema-driven dynamic editors", body: "Each of 4 application types has its own section schema with conditional fields, repeatable groups, and per-field validation. Building a single editor layout that renders correctly for all types required deep schema abstraction." },
          { title: "Streaming AI across two runtimes", body: "Validation and writing features stream character-by-character from FastAPI through the Next.js proxy to the browser. Managing backpressure, error states, and UI rendering across this chain was a significant engineering challenge." },
          { title: "Real-time collaboration at application level", body: "Socket.IO rooms per application, coordinated between the Python backend and the Next.js frontend, with read state, unread counts, and live broadcasting, all without race conditions." },
          { title: "RAG over complex regulatory documents", body: "EU funding guidelines are long, dense, and full of cross-references. Building RAG that retrieves the right chunks required careful chunking strategy, metadata filtering, and prompt engineering." },
        ],
      },
      {
        label: "Product Screens",
        heading: "Inside the product",
        screens: [
          { src: "/assets/work/structura/open-calls.png", caption: "OPEN-CALL DISCOVERY — 800+ CALLS RANKED BY FIT IN ~12s" },
          { src: "/assets/work/structura/dashboard.png", caption: "APPLICATIONS DASHBOARD — COMPLETION TRACKING & WORD EXPORT" },
        ],
      },
      {
        pair: [
          {
            label: "Frontend Stack",
            heading: "Next.js App",
            stack: [
              { label: "Framework", value: "Next.js 15.5, React 19.1, TypeScript" },
              { label: "Styling", value: "Tailwind CSS 4, Headless UI, Lucide" },
              { label: "i18n", value: "next-intl (localisation)" },
              { label: "Auth", value: "NextAuth + @auth/mongodb-adapter" },
              { label: "Forms", value: "react-hook-form + Zod validation" },
              { label: "Real-time", value: "Socket.IO client" },
              { label: "Exports", value: "docx, pdfkit, mammoth, xlsx" },
              { label: "Storage", value: "AWS SDK (S3 uploads + signed URLs)" },
            ],
          },
          {
            label: "Backend Stack",
            heading: "FastAPI AI Service",
            stack: [
              { label: "Framework", value: "FastAPI, Pydantic, Uvicorn/Gunicorn" },
              { label: "AI", value: "LangChain (OpenAI + Anthropic + MongoDB)" },
              { label: "Vector DB", value: "Pinecone (vector search + RAG)" },
              { label: "LLMs", value: "Anthropic Claude + OpenAI GPT SDKs" },
              { label: "Database", value: "Motor + PyMongo (async MongoDB)" },
              { label: "Real-time", value: "python-socketio (WebSocket rooms)" },
              { label: "Parsing", value: "PyMuPDF, pdfminer, mammoth, python-docx" },
              { label: "Email", value: "boto3 (AWS S3), Resend" },
            ],
          },
        ],
      },
      {
        label: "The Outcome",
        heading: "Production-grade SaaS covering the entire EU grant lifecycle",
        body: "Structura delivered a production-grade SaaS platform that covers the entire EU grant lifecycle, from first idea to post-award compliance. Organisations can author applications faster with AI assistance, collaborate with stakeholders in real time, and manage funded projects without switching tools. The compliance module alone addresses a gap that no existing tool on the market fills properly.",
        highlight:
          "This is my most complex project to date: 12 months, two runtime environments, 6+ AI pipelines, real-time collaboration, regulatory compliance, and a full project management suite. It shows I can architect, build, and ship enterprise-grade AI SaaS products end to end, at any level of complexity.",
      },
    ],
  },

  smartchat: {
    title: "SmartChat — AI Chatbot Platform for Businesses",
    subtitle:
      "A no-code SaaS platform that lets any business create, deploy, and monitor AI-powered chatbots trained on their own data, no developer needed.",
    tags: ["AI Development", "RAG / LLM", "Full Stack", "Next.js 14", "Pinecone", "AWS S3", "MongoDB"],
    stats: [
      { num: "6", label: "Core modules built" },
      { num: "4", label: "Deployment methods" },
      { num: "RAG", label: "AI architecture" },
      { num: "3", label: "User roles supported" },
    ],
    sections: [
      {
        label: "The Problem",
        heading: "Custom chatbots were expensive and generic",
        body: "Businesses were spending thousands of dollars and months of time hiring agencies to build custom chatbots, only to end up with generic, pre-programmed bots that couldn't handle real customer questions. Smaller businesses simply couldn't afford this at all.",
        highlight:
          "“The traditional method of developing custom chatbots through external providers is time-consuming, expensive, and produces bots that don't truly understand the business.”",
      },
      {
        label: "What I Built",
        heading: "End-to-end SaaS AI chatbot platform",
        body: "A complete web-based SaaS platform where business owners can create AI chatbots trained on their own documents, no technical knowledge required. Built end-to-end: authentication, admin panels, AI pipeline, analytics, and deployment infrastructure.",
        steps: [
          { title: "Business uploads their documents", body: "PDFs, knowledge base files, and product info go into the platform through a clean admin panel." },
          { title: "RAG pipeline processes the data", body: "Documents are vectorized using embeddings and stored in Pinecone vector database for fast semantic retrieval." },
          { title: "AI generates context-aware responses", body: "LangChain retrieves relevant chunks and GPT generates accurate, business-specific answers when customers ask questions." },
          { title: "Business deploys in minutes", body: "Via hosted URL, QR code, website embed code, WordPress plugin, or API integration. Four deployment paths." },
          { title: "Analytics dashboard tracks everything", body: "Total chats, active users, sentiment scores, unresolved queries, and AI-generated improvement recommendations." },
        ],
      },
      {
        pair: [
          {
            label: "Key Features",
            heading: "What's inside",
            features: [
              "No-code chatbot creation interface",
              "RAG-powered context-aware responses",
              "Fully customisable chatbot UI (logo, colours, tagline)",
              "4 deployment options: API, Hosted UI, Embed, QR",
              "Real-time analytics dashboard",
              "AI-generated chat insights & recommendations",
              "Multi-role admin system (admin / agent / user)",
            ],
          },
          {
            label: "Tech Stack",
            heading: "Built with",
            stack: [
              { label: "Frontend", value: "Next.js 14, Tailwind CSS, Chart.js" },
              { label: "Backend", value: "Node.js, Python 3.11, REST APIs" },
              { label: "AI / ML", value: "LangChain, RAG, GPT, Hugging Face" },
              { label: "Database", value: "MongoDB Atlas, Pinecone (Vector DB)" },
              { label: "Storage", value: "AWS S3" },
              { label: "Infrastructure", value: "Digital Ocean" },
            ],
          },
        ],
      },
      {
        label: "The Outcome",
        heading: "Production-ready multi-tenant SaaS",
        body: "SmartChat delivered a production-ready, multi-tenant SaaS platform that competes directly with tools like ChatPDF and Chatbot.com, but with superior integration options, real-time analytics, and true document-based AI responses. Any business can go from zero to a live AI chatbot in under 10 minutes, without writing a single line of code.",
        highlight:
          "This project demonstrates full end-to-end AI product development, from RAG architecture and LLM integration to SaaS infrastructure, multi-role admin systems, and production deployment.",
      },
    ],
  },

  documind: {
    title: "DocuMind — AI-Powered Policy & Knowledge Assistant",
    subtitle:
      "An intelligent internal assistant built for a large organisation, enabling staff to instantly query complex policy documents, compliance rules, and institutional knowledge, powered by RAG and LLM technology.",
    tags: ["AI Development", "RAG / LLM", "Knowledge Management", "Enterprise"],
    stats: [
      { num: "6", label: "Core modules built" },
      { num: "3-tier", label: "System architecture" },
      { num: "RAG", label: "AI engine powering responses" },
      { num: "2-layer", label: "AI query classification" },
    ],
    sections: [
      {
        label: "The Problem",
        heading: "Hundreds of policies, zero easy access",
        body: "Large organisations often have hundreds of internal policies: scholarship rules, compliance guidelines, leave procedures, grant regulations, all scattered across documents nobody reads. Staff waste hours searching for answers, often miss entitlements, and overwhelm support offices with repetitive questions.",
        highlight:
          "“Staff faced challenges navigating complex institutional policies, leading to missed entitlements and suboptimal decisions, all due to fragmented access and time constraints.”",
      },
      {
        label: "What I Built",
        heading: "Full-stack AI knowledge platform with dual-AI engine",
        body: "A full-stack AI assistant platform where admin teams upload and manage policy documents, and staff interact with a smart chatbot that answers policy questions instantly, with source documents shown as evidence. Two-layer AI architecture: fast FAQ matching for common questions, and full RAG + LLM generation for complex queries.",
        steps: [
          { title: "Admin uploads policy documents", body: "PDFs stored in MongoDB via GridFS and simultaneously vectorised into Pinecone for semantic search." },
          { title: "Smart query classification", body: "Every user question is first classified by a trained ML model: Is it a known FAQ, a valid policy query, or an irrelevant question?" },
          { title: "Dual AI response engine", body: "FAQ queries return instant predefined answers; non-FAQ valid queries go through the LangChain RAG pipeline, which retrieves relevant policy chunks and generates accurate LLM responses." },
          { title: "Source documents shown as proof", body: "Users see not just the answer but the exact policy document it came from, building trust and transparency." },
          { title: "Feedback loop built in", body: "Users submit complaints or suggestions; admins receive notifications, review feedback, and respond, all within the platform." },
          { title: "Policy change notifications", body: "When admins update or add a document, users are automatically notified, keeping everyone aligned with the latest policies." },
        ],
      },
      {
        pair: [
          {
            label: "Key Features",
            heading: "What's inside",
            features: [
              "Two-layer AI: FAQ classifier + RAG pipeline",
              "Policy document management (CRUD)",
              "Multi-turn conversation with chat history",
              "Source document shown with every RAG answer",
              "Real-time policy update notifications",
              "Feedback submission & admin response system",
              "Role-based access control (Admin / User)",
              "Document search & filter system",
            ],
          },
          {
            label: "Tech Stack",
            heading: "Built with",
            stack: [
              { label: "Frontend", value: "Next.js 14, Tailwind CSS 3.0" },
              { label: "Backend", value: "Node.js (ES6), Python 3.11" },
              { label: "AI / ML", value: "LangChain, RAG, Hugging Face (classification)" },
              { label: "Vector DB", value: "Pinecone (semantic search)" },
              { label: "Database", value: "MongoDB + GridFS (document storage)" },
              { label: "Infrastructure", value: "Hetzner Cloud Server" },
              { label: "Design", value: "Figma (UI prototyping)" },
            ],
          },
        ],
      },
      {
        label: "What Made This Complex",
        heading: "Not just a chatbot",
        body: "Most chatbots just do one thing: answer from a knowledge base. DocuMind required a significantly more sophisticated architecture.",
        highlight:
          "The dual-AI approach means the system first classifies intent using a trained Hugging Face model, then routes to either a fast FAQ engine or a full RAG pipeline. Users get instant answers for common questions, while complex policy queries are handled with full LLM reasoning and document citations.",
        body2:
          "This combination of ML classification, LangChain RAG, MongoDB GridFS, and Pinecone vector search, all on a production cloud server, makes it a fully enterprise-grade AI knowledge system rather than a demo or prototype.",
      },
      {
        label: "The Outcome",
        heading: "Hours of searching replaced by instant answers",
        body: "DocuMind replaced hours of manual policy searching with instant, accurate, source-backed AI answers. The organisation's support team saw a significant reduction in repetitive policy queries. Staff could now self-serve answers to complex compliance and procedural questions with confidence, because the system always showed the source document behind every response.",
        highlight:
          "This project demonstrates my ability to build enterprise AI systems with custom ML pipelines, document intelligence, multi-role platforms, and real-time notification systems, fully deployed on cloud infrastructure.",
      },
    ],
  },

  rebhub: {
    title: "RebHub — AI-Powered Real Estate Platform & CRM",
    subtitle:
      "A complete real estate ecosystem: a consumer-facing property search portal powered by an AI chatbot, backed by a full-featured CRM for managing leads, listings, agents, sales pipelines, and projects. Built for the Pakistan real estate market.",
    tags: ["Real Estate", "AI Chatbot", "CRM / CMS", "Full Stack", "Live Product"],
    liveUrl: "https://rebhub-web-client.vercel.app/",
    stats: [
      { num: "2-in-1", label: "Public portal + CRM platform" },
      { num: "AI", label: "Property search chatbot" },
      { num: "Multi-role", label: "Admin, agent, buyer access" },
      { num: "Full", label: "Lead-to-sale pipeline" },
    ],
    sections: [
      {
        label: "The Challenge",
        heading: "Two products. One codebase. One seamless system.",
        body: "The client needed more than just a property listings website. They needed a complete operational system: a public-facing portal that could attract and convert buyers and renters, combined with a powerful internal CRM that their agents and sales team could use to manage leads, track deals, and oversee inventory.",
        highlight:
          "“Most real estate platforms are either pretty frontends with no backend depth, or clunky internal tools with no public presence. RebHub needed to be both, and they needed to work together.”",
      },
      {
        pair: [
          {
            label: "Public Portal",
            heading: "Consumer-facing property search",
            steps: [
              { title: "Property search engine", body: "Filter by city, type, area, price, beds & baths across buy, rent and project categories." },
              { title: "AI property chatbot", body: "Users describe what they want in plain words and the chatbot surfaces relevant listings. “Tired of searching? Ask directly.”" },
              { title: "Projects & development showcase", body: "Dedicated section for off-plan and new development projects (DHA, Bahria, Lake City, Etihad Town etc.)." },
              { title: "Agent directory", body: "Browsable agent profiles with listings and contact options." },
              { title: "Blog & content CMS", body: "Market insights and real estate content managed from the backend." },
            ],
          },
          {
            label: "CRM / Admin Platform",
            heading: "Internal sales operations",
            steps: [
              { title: "Lead management", body: "Capture, assign, and track every lead from the portal through to conversion with full status history." },
              { title: "Sales pipeline", body: "Visualise deals in progress, track negotiations, follow-ups, and closings per agent and per property." },
              { title: "Property inventory CMS", body: "Add, edit, publish or unpublish listings with full media management (images, floor plans, details)." },
              { title: "Agent & team management", body: "Onboard agents, assign listings and leads, set roles and permissions, track individual performance." },
              { title: "Projects management", body: "Manage off-plan developments, track units, availability and buyer interest per project." },
            ],
          },
        ],
      },
      {
        label: "CRM Modules",
        heading: "What the internal platform covers",
        cards: [
          { icon: "👤", title: "Lead Management", body: "Capture leads from portal enquiries, assign to agents, track status (new → contacted → qualified → closed), full communication log." },
          { icon: "📊", title: "Sales Pipeline", body: "Visual deal tracking per agent, stage-based pipeline, follow-up reminders, deal value tracking and conversion reporting." },
          { icon: "🏠", title: "Property CMS", body: "Full CRUD for listings: add properties with images, specs, location, and pricing. Publish and unpublish controls. Syncs to the public portal instantly." },
          { icon: "🏗️", title: "Projects CMS", body: "Manage development projects, unit availability, floor plans, pricing tiers, and project status updates visible on the public portal." },
          { icon: "🧑‍💼", title: "Agent Management", body: "Onboard agents, assign listings and leads, set roles and permissions, track individual performance, manage public agent profiles." },
          { icon: "📝", title: "Blog / Content CMS", body: "Create and manage market insight articles, news, and guides that publish to the public portal's blog section." },
        ],
      },
      {
        pair: [
          {
            label: "Tech Stack",
            heading: "Built with",
            stack: [
              { label: "Frontend", value: "React.js + Next.js (App Router)" },
              { label: "Styling", value: "Tailwind CSS, responsive design" },
              { label: "AI Chatbot", value: "Conversational property search assistant" },
              { label: "CRM Backend", value: "Node.js REST API" },
              { label: "Database", value: "MongoDB (listings, leads, users)" },
              { label: "Auth", value: "Role-based (Admin / Agent / User)" },
              { label: "Deployment", value: "Vercel (frontend)" },
            ],
          },
          {
            label: "Key Highlights",
            heading: "What sets it apart",
            features: [
              "AI chatbot integrated into property search flow",
              "Real-time sync between CMS and public portal",
              "Multi-role access: admin, agent, buyer",
              "Bilingual UI (English + localisation-ready)",
              "Favourite properties saved per user account",
              "Filtered search across buy, rent & projects",
              "Full lead-to-close CRM pipeline",
              "Live, production-deployed product",
            ],
          },
        ],
      },
      {
        label: "The Outcome",
        heading: "A complete business ecosystem, live in production",
        body: "RebHub launched as a fully operational real estate platform serving the Pakistan property market, with a public portal that drives lead generation and an internal CRM that gives the sales team full visibility over their pipeline. The AI chatbot reduced search friction significantly, letting users describe what they want in plain language instead of navigating filters.",
        highlight:
          "This project demonstrates my ability to build complete business ecosystems, not just websites. A consumer-facing product and a full internal CRM, built together, fully integrated, and live in production.",
      },
    ],
  },

  signalcast: {
    title: "SignalCast — Real-Time Voice AI & 3D Avatar Translation Engine",
    subtitle:
      "A real-time voice AI system that extracts, transcribes, and translates spoken audio from video content, then drives a 3D animated avatar to perform the translation live, in sync with the video.",
    tags: ["Voice AI", "Real-time Audio Processing", "3D Avatar Animation", "Browser Extension", "Accessibility Tech"],
    stats: [
      { num: "7", label: "Core modules built" },
      { num: "Real-time", label: "Audio-to-avatar sync" },
      { num: "Multi-lang", label: "Audio transcription support" },
      { num: "3D", label: "Live avatar rendering" },
    ],
    sections: [
      {
        label: "The Problem",
        heading: "1.5 billion people can't access online video",
        body: "Over 1.5 billion people worldwide have hearing loss, yet the majority of online video content remains completely inaccessible to them. Existing solutions like closed captions are static, imprecise, and fail to convey the nuance of spoken language. There was no real-time, context-aware, multilingual system that could translate video audio into dynamic sign language, especially one that could handle multiple languages and operate directly inside a browser.",
        highlight:
          "“There is no complete, globally applicable solution that provides real-time, context-aware sign language translation for video content across multiple languages.”",
      },
      {
        label: "The Voice AI Pipeline",
        heading: "Video URL → live 3D sign language",
        pipeline: ["🎬 Video URL", "🔊 Audio Extract", "📝 Speech-to-Text", "🧠 NLP Context", "🤟 Sign Mapping", "🧍 3D Avatar", "📺 Live Overlay"],
        steps: [
          { title: "User pastes a video URL", body: "The browser extension validates the link, detects video format and metadata, and initiates the processing pipeline." },
          { title: "Audio is extracted and preprocessed", body: "Background noise is filtered, audio quality optimised using OpenCV pipelines to maximise transcription accuracy." },
          { title: "Speech-to-text via Groq Cloud API", body: "Multilingual audio transcribed with high accuracy in real time; NLTK cleans and normalises the text output." },
          { title: "Context-aware sign language mapping", body: "The system analyses sentence context using NLP rather than word-by-word mapping, producing natural and semantically accurate sign sequences." },
          { title: "3D avatar rendered via Three.js + Unity", body: "A real-time animated character performs the sign language gestures, rigged and synced frame-by-frame with the video timeline." },
          { title: "Live overlay inside the video frame", body: "The avatar is rendered directly within the video player, not as a separate window, which creates a seamless, immersive viewing experience." },
        ],
      },
      {
        pair: [
          {
            label: "Key Features",
            heading: "What's inside",
            features: [
              "Real-time audio extraction from video URLs",
              "Multilingual speech-to-text transcription",
              "Context-aware NLP sign language mapping",
              "Live 3D avatar animation (Unity + Three.js)",
              "In-video overlay rendering (browser-native)",
              "Real-time video-avatar sync engine",
              "User dashboard with history & controls",
              "Social login + account management",
            ],
          },
          {
            label: "Tech Stack",
            heading: "Built with",
            stack: [
              { label: "Frontend", value: "React JS 18.2, HTML5, CSS3" },
              { label: "3D Rendering", value: "Three.js 0.159, Unity 2022.3" },
              { label: "Voice AI", value: "Groq Cloud API 4.2 (speech-to-text)" },
              { label: "NLP", value: "NLTK 3.8 (text processing & context)" },
              { label: "Vision / ML", value: "OpenCV 4.8 (audio optimisation)" },
              { label: "Backend", value: "Python 3.13" },
              { label: "Design", value: "Figma, PyCharm" },
            ],
          },
        ],
      },
      {
        label: "Engineering Challenge",
        heading: "Why this was technically hard",
        body: "Most voice AI projects stop at transcription. SignalCast goes 3 layers deeper, and each layer introduces significant engineering complexity.",
        highlight:
          "The hardest part wasn't speech-to-text. It was synchronising a live 3D avatar with real-time buffered audio inside a browser extension, while maintaining context-aware (not word-for-word) sign language accuracy across multiple languages simultaneously.",
        body2:
          "Solving the real-time buffering challenge, where sign language gestures must stay in sync with the video even as transcription lag occurs, required building a custom frame-sync engine between the Python backend and the Three.js/Unity 3D renderer.",
      },
      {
        label: "The Outcome",
        heading: "A genuinely novel voice AI system",
        body: "SignalCast delivered a working real-time voice AI system that converts any video's spoken audio into live 3D sign language, rendered directly inside the browser, in sync with the video. A genuinely novel approach to voice AI that goes far beyond transcription into real-time motion generation and avatar animation.",
        highlight:
          "This project showcases my capability to build end-to-end voice AI pipelines, from audio extraction and multilingual speech processing to NLP context analysis and real-time 3D rendering. The same pipeline architecture applies directly to voice agents, AI avatars, real-time translation tools, and multimedia automation systems.",
      },
    ],
  },

  "yarns-and-wools": {
    title: "Yarns & Wools — Multi-Portal E-Commerce Marketplace",
    subtitle:
      "A three-portal e-commerce ecosystem: a fully controllable consumer storefront, a brand self-service portal for onboarding and managing products, and a super admin panel to control the entire platform, content, policies, and approvals.",
    tags: ["E-Commerce", "Multi-Portal Platform", "Brand Marketplace", "Next.js + Firebase", "Live Product"],
    liveUrl: "https://www.yarnsandwool.com/",
    stats: [
      { num: "3", label: "Separate portals built" },
      { num: "Multi", label: "Brand marketplace" },
      { num: "100%", label: "CMS-controlled storefront" },
      { num: "Live", label: "Production since 2026" },
    ],
    sections: [
      {
        label: "The Challenge",
        heading: "Three portals. One unified ecosystem.",
        body: "Yarns & Wools needed more than a standard e-commerce site. As a multi-brand textile platform, they needed a complete ecosystem: a consumer storefront they could control without developers, a self-service portal where brands could register and manage their own products, and a super admin system to oversee everything.",
        highlight:
          "Three distinct portals. One unified platform. Every portal talks to the same Firebase backend in real time. Brands update their stock and it shows on the storefront instantly. Admins approve a brand and their page goes live immediately.",
      },
      {
        label: "Portal 1",
        heading: "Consumer Storefront — yarnsandwool.com",
        steps: [
          { title: "Fully CMS-controlled landing page", body: "Every section (hero, spotlight products, banners, promises, newsletter) is editable by super admin without touching code." },
          { title: "Brand discovery pages", body: "Each approved brand gets a dedicated sub-page with their full product catalogue, brand story, and filtered shop experience." },
          { title: "Shop & category browsing", body: "Filter by brand, category, price range, fabric type. Full product detail pages with images, specs, and stock status." },
          { title: "Cart & checkout flow", body: "Add to cart, quantity management, checkout with shipping details, order confirmation, and order history per customer." },
          { title: "Policies & legal pages", body: "Shipping & returns, terms & conditions, and contact, all editable from the super admin CMS." },
          { title: "WhatsApp integration", body: "Direct customer support link, newsletter signup, responsive across all devices." },
        ],
      },
      {
        label: "Portal 2",
        heading: "Brand Portal — Self-Service Management",
        steps: [
          { title: "Brand registration & onboarding", body: "Brands apply to join with their profile, logo, description, and contact info, which goes to the super admin for approval." },
          { title: "Product management", body: "Add, edit, delete products with images, descriptions, pricing, variants (size, colour), and category assignment." },
          { title: "Category management", body: "Brands create and manage their own product categories, kept separate per brand in the marketplace." },
          { title: "Stock & inventory control", body: "Manage stock levels per product variant, get low-stock visibility, toggle product visibility on/off without deleting." },
          { title: "Brand profile management", body: "Update brand story, cover images, logo, and public-facing brand page content that appears on the storefront." },
          { title: "Order visibility", body: "Brands see orders placed for their products with customer and fulfilment details relevant to their inventory." },
        ],
      },
      {
        label: "Portal 3",
        heading: "Super Admin Panel — Full Platform Control",
        steps: [
          { title: "Brand approval management", body: "Review brand applications, approve or reject with feedback, control which brands are visible on the storefront." },
          { title: "Landing page CMS", body: "Edit every section of the consumer storefront: hero banners, spotlight products, promotional content, and brand ordering. No code required." },
          { title: "Policy & content management", body: "Update shipping policy, return rules, terms & conditions, and legal pages directly from the admin panel." },
          { title: "Platform-wide product oversight", body: "View and manage all products across all brands, override visibility, flag issues, and ensure catalogue quality." },
          { title: "Order & customer management", body: "Full order history, customer records, and fulfilment status across all brands in one dashboard." },
          { title: "Platform settings & configuration", body: "Manage global settings: shipping thresholds, exchange policies, featured brands, promotional banners, and site-wide announcements." },
        ],
      },
      {
        pair: [
          {
            label: "Tech Stack",
            heading: "Built with",
            stack: [
              { label: "Frontend", value: "Next.js (App Router), React, TypeScript" },
              { label: "Styling", value: "Tailwind CSS, fully responsive" },
              { label: "Backend & DB", value: "Firebase (Firestore, Auth, Storage)" },
              { label: "Real-time", value: "Firestore live listeners (instant sync)" },
              { label: "Auth", value: "Firebase Auth (multi-role: admin, brand, customer)" },
              { label: "Deployment", value: "Vercel (frontend + SSR)" },
            ],
          },
          {
            label: "Why Firebase",
            heading: "The right tool for this",
            features: [
              "Real-time sync: brand updates stock, storefront updates instantly",
              "Multi-role auth serving three portals cleanly (admin / brand / customer)",
              "Firestore rules enforce data security at the database level",
              "Firebase Storage with CDN for product images and brand assets",
              "Handles traffic spikes without infrastructure management overhead",
            ],
          },
        ],
      },
      {
        label: "The Outcome",
        heading: "Live marketplace with zero developer dependency",
        body: "Yarns & Wools launched as a fully operational multi-brand e-commerce marketplace. The super admin can update the landing page, approve brands, and manage policies without involving a developer. Brands self-manage their catalogue and stock. Customers get a curated, branded shopping experience with real-time inventory. Three portals. Zero developer dependency for day-to-day operations.",
        highlight:
          "This project demonstrates my ability to build complete multi-portal e-commerce ecosystems, not just storefronts. A consumer-facing marketplace, a brand self-service portal, and a super admin control centre, all live, all integrated, all running in production.",
      },
    ],
  },
};
